import { z } from 'zod';
import {
  IdSchema,
  InstantSchema,
  PermissionSchema,
  RoleSchema,
  StageSchema,
  ViewKeySchema,
} from './common.schema';
import { ActivityEntrySchema } from './activity.schema';
import { LabSettingsSchema } from './settings.schema';

export const DepartmentSchema = z.union([
  StageSchema,
  z.literal('management'),
  z.literal('reception'),
]);

export const UserSchema = z.object({
  id: IdSchema,
  name: z.string(),
  username: z.string(),
  title: z.string(),
  role: RoleSchema,
  department: DepartmentSchema,
  /** CAD/CAM staff who also do intake. */
  isIntakeOperator: z.boolean(),
  /**
   * A department supervisor: a technician who also approves Final Check.
   * Section 11 of the request of 20 September 2026.
   *
   * A flag, as `isIntakeOperator` is, and not a role. It resolves into the
   * `finalcheck` permission and the `finalcheck` view ON THE SESSION, through the
   * same server-side resolution the per-person exceptions use, so the matrix
   * still reads "Final Check: Management" and the exception sits on the person.
   * `SessionSchema` needs nothing: the permission and the view arrive in the
   * lists it already carries.
   */
  isSupervisor: z.boolean(),
  disabled: z.boolean(),
  createdAt: InstantSchema,
  /**
   * True when this person has exceptions to their role: a permission or a screen
   * granted or denied on their own record. So the team table can mark them
   * without fetching every record. Request of 13 September 2026, section 2.3.
   */
  hasAccessOverrides: z.boolean(),
  /**
   * Where the person's photograph is served, or null for none, when the screens
   * draw their initials. Request of 4 October 2026 (second), section 7.
   */
  avatarUrl: z.string().nullable().optional(),
});

/** A reduced list for assignment pickers, for callers without `admin`. */
export const UserSummarySchema = z.object({
  id: IdSchema,
  name: z.string(),
  department: DepartmentSchema,
});

/**
 * A person in an assignment list: `GET /kits/{kitId}/assignees` and
 * `GET /stages/{stage}/assignees`. With how many cases they hold now, their
 * queue, so whoever assigns a case or sends one for rework can give it to the
 * person with least. The cases assigned to them that are still in the lab: not
 * archived, not dispatched. Sections 7 and 8 of the request of 5 October 2026.
 */
export const AssigneeSchema = UserSummarySchema.extend({
  activeCases: z.number().int().nonnegative(),
});

/**
 * `permissions` and `views` are resolved BY THE SERVER for this user. The
 * frontend must not compute them from a role table it keeps locally, because
 * the access matrix is editable at runtime. Section 7.2.
 */
/**
 * The lab geofence, resolved by the server for this session.
 *
 * It rides on the session rather than sitting behind its own public endpoint,
 * because the check needs the caller's ROLE to know whether it applies, and the
 * role is only known once they have authenticated. That also keeps the lab's
 * coordinates behind the login rather than on the open internet.
 */
export const LabGeofenceSchema = z.object({
  enabled: z.boolean(),
  latitude: z.number().min(-90).max(90),
  longitude: z.number().min(-180).max(180),
  radiusMetres: z.number().int().positive(),
  appliedRoles: z.array(RoleSchema),
});

export const SessionSchema = z.object({
  user: UserSchema,
  role: RoleSchema,
  permissions: z.array(PermissionSchema),
  views: z.array(ViewKeySchema),
  homeView: ViewKeySchema,
  /** Used to correct client clock drift on shared floor devices, section 9. */
  serverTime: InstantSchema,
  geofence: LabGeofenceSchema,
  /** The routing mode, beside the geofence and for the same reason. */
  settings: LabSettingsSchema,
  /**
   * True while this person is still on the password everybody was given. The
   * screen asks for a new one before anything else, and the server refuses
   * everything but changing it and signing out. Absent means false. Section 15
   * of the review of 2 October 2026.
   */
  mustChangePassword: z.boolean().optional(),
});

export const LoginSchema = z.object({
  username: z.string().min(1, 'Enter your user name'),
  password: z.string().min(1, 'Enter your password'),
  /**
   * Asks for a 30 day refresh cookie instead of a session one. The server
   * decides: the two bench roles share a tablet and never get one. Section 4
   * of the request of 13 September 2026.
   */
  remember: z.boolean().optional(),
});

/**
 * The access token is short lived and held in memory only. The refresh token is
 * an HttpOnly, Secure, SameSite=Strict cookie scoped to /api/v1/auth and never
 * appears in a payload. Section 7.2, and item 12 of section 17.
 */
export const AuthResultSchema = z.object({
  accessToken: z.string(),
  expiresIn: z.number().int().positive(),
  /** Whether the server actually granted a persistent session. */
  persistent: z.boolean(),
  session: SessionSchema,
});

export const ChangePasswordSchema = z.object({
  currentPassword: z.string().min(1),
  newPassword: z.string().min(8, 'Use at least eight characters'),
});

/**
 * `POST /auth/sign-in-location`. Where a sign in came from, for the log.
 * Section 28 of the review of 2 October 2026.
 *
 * The browser still decides whether a bench role may sign in; this tells the
 * server what it saw, so a manager can read where people signed in from.
 * Null coordinates mean the browser gave no location.
 */
export const SignInLocationSchema = z.object({
  latitude: z.number().min(-90).max(90).nullable(),
  longitude: z.number().min(-180).max(180).nullable(),
});

/**
 * `POST /users/{id}/password`. A manager sets somebody else's password, and
 * the person is signed out everywhere. Your own goes through
 * `/auth/change-password`, which asks for the current one.
 */
export const SetUserPasswordSchema = z.object({
  newPassword: z.string().min(8, 'Use at least eight characters'),
});

export const CreateUserSchema = z.object({
  name: z.string().min(1, 'Enter a name'),
  username: z.string().min(1, 'Enter a user name'),
  title: z.string().min(1, 'Enter a job title'),
  role: RoleSchema,
  department: DepartmentSchema,
  password: z.string().min(8, 'Use at least eight characters'),
  isIntakeOperator: z.boolean().optional(),
  /** Also approves Final Check. Section 11 of the request of 20 September 2026. */
  isSupervisor: z.boolean().optional(),
});

export const UpdateUserSchema = CreateUserSchema.partial().extend({
  /**
   * How to reach the person, shown on their profile to them and to management.
   * Sent blank, it is cleared. Request of 4 October 2026 (second), section 7.
   */
  phone: z.string().max(32).optional(),
  email: z.string().max(120).optional(),
});

/**
 * `GET /users/{id}/profile`. Your own, or anybody's for management.
 * Request of 4 October 2026 (second), section 7.
 *
 * The summary is the three counts the request names, from the same records the
 * staff report counts, for the chosen period: cases worked (a case worked at two
 * benches is one), the teeth on them (each case once, since the lab's request
 * of 17 October 2026, item 6), and job orders created. No rates and no
 * comparison with anybody else.
 */
export const ProfileSchema = z.object({
  user: z.object({
    id: IdSchema,
    name: z.string(),
    username: z.string(),
    title: z.string(),
    role: RoleSchema,
    department: DepartmentSchema,
    isSupervisor: z.boolean(),
    isIntakeOperator: z.boolean(),
    disabled: z.boolean(),
    avatarUrl: z.string().nullable(),
    phone: z.string().nullable(),
    email: z.string().nullable(),
    createdAt: InstantSchema,
    lastLoginAt: InstantSchema.nullable(),
  }),
  range: z.enum(['all', 'today', 'week', 'month']),
  /**
   * Null for a technician or CAD / CAM caller, their own profile included,
   * unless they hold a screen of performance figures (`staff` or `perf`).
   */
  summary: z
    .object({
      casesWorked: z.number().int().nonnegative(),
      tasksCompleted: z.number().int().nonnegative(),
      teethWorked: z.number().int().nonnegative(),
      jobOrdersCreated: z.number().int().nonnegative(),
    })
    .nullable(),
  /** The cases on their clock or held open by them, the newest first. */
  current: z.array(
    z.object({
      kitId: IdSchema,
      jobOrderNo: z.string(),
      stage: StageSchema,
      running: z.boolean(),
      startedAt: InstantSchema,
    }),
  ),
  /** What they did last, from the activity log, the newest first. */
  recent: z.array(ActivityEntrySchema),
});

/** `POST /users/{id}/avatar`. */
export const AvatarResultSchema = z.object({ avatarUrl: z.string() });

/**
 * One stage a person worked, on one case. `GET /users/{userId}/cases`.
 *
 * A row per piece of work, not per case, because that is the question the
 * screen answers: a manager opening somebody's record wants to see what they
 * did, and a case they took twice after a rework is two entries, honestly.
 *
 * `completedAt` and `activeSeconds` are null while the task is still running.
 * `currentStage` is where the CASE is now, which is usually not the stage this
 * person worked, and null once the order has been dispatched.
 */
export const UserCaseRowSchema = z.object({
  kitId: IdSchema,
  jobOrderNo: z.string(),

  // Section 6 item 1. Absent for a caller without seeIdentity.
  dentistName: z.string().optional(),
  patientName: z.string().optional(),

  product: z.string(),
  units: z.number().int().nonnegative(),

  /** The bench this person worked, which is what their record is about. */
  stage: StageSchema,
  startedAt: InstantSchema,
  completedAt: InstantSchema.nullable(),
  /** Active time, excluding idle. Null while the task is still running. */
  activeSeconds: z.number().int().nonnegative().nullable(),
  rework: z.boolean(),

  /** Where the case sits now. Null once it has left the building. */
  currentStage: StageSchema.nullable(),
});

export type Session = z.infer<typeof SessionSchema>;
export type User = z.infer<typeof UserSchema>;
export type UserCaseRow = z.infer<typeof UserCaseRowSchema>;
